import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import Link from "next/link";
import AvatarPopover from "./avatar-popover";
import { getRandomAvatar, getUserName } from "@/utils/utils";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Avatar, AvatarImage } from "./ui/avatar";
import { getOrganizationMemberInvitationDetails } from "@/services/member.client";
import { useStoreHydration } from "@/hooks/useStoreHydration";
import { LoaderCircle } from "lucide-react";
import { getUploadUrl } from "@/services/upload.client";
import { User } from "@/types/user";
import Image from "next/image";
import { deleteCookie } from "@/lib/cookies";
import { logout } from "@/app/actions";
import { OrganizationMemberInviteUpdateData } from "@/types/organization";
import { useRouter } from "next/navigation";

interface InviteAcceptDialogProps {
  controlledOpen?: boolean;
  user: User | null;
  invitation: {
    orgId: string;
    userId: string;
    token: string;
  };
  onAccept: (updateData: OrganizationMemberInviteUpdateData) => Promise<void>;
  onDecline: (
    updateData: OrganizationMemberInviteUpdateData | null,
  ) => Promise<void>;
  onOpenChange?: (open: boolean) => void;
}

export default function InviteAcceptDialog({
  controlledOpen,
  user,
  invitation,
  onAccept,
  onDecline,
  onOpenChange,
}: InviteAcceptDialogProps) {
  const router = useRouter();
  const isHydrated = useStoreHydration();
  const [internalOpen, setInternalOpen] = useState(false);
  const [organizationDetails, setOrganizationDetails] = useState<{
    name: string;
    logo: string;
  } | null>(null);
  const [inviterDetails, setInviterDetails] = useState<{
    name: string;
    email: string;
  } | null>(null);
  const [userDetails, setUserDetails] = useState<{
    id: string;
    name: string;
    avatar: string;
  } | null>(null);
  const [isDisabled, setIsDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isInvalid, setIsInvalid] = useState(false);
  const [isUserMismatch, setIsUserMismatch] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: invitation?.userId || "",
    avatar: `/avatars/${getRandomAvatar()}`,
  });

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = useCallback(
    (open: boolean) => {
      if (controlledOpen === undefined) {
        setInternalOpen(open);
      }
      if (onOpenChange) {
        onOpenChange(open);
      }
    },
    [controlledOpen, onOpenChange],
  );

  useEffect(() => {
    if (!isHydrated || !invitation) return;

    const initInvitation = async () => {
      try {
        const details = await getOrganizationMemberInvitationDetails(
          invitation.orgId,
          invitation.userId,
          invitation.token,
        );

        if (!details) {
          setIsInvalid(true);
          setIsLoading(false);
          deleteCookie("inviteParams", "/");
          return;
        }

        // Handle custom logo
        if (
          details.organizationLogo &&
          details.organizationLogo.includes("organizations/")
        ) {
          details.organizationLogo = await getUploadUrl(
            details.organizationLogo,
            {
              orgId: invitation.orgId,
              userId: invitation.userId,
              token: invitation.token,
            },
          );
        }

        // Set the organization name and logo
        setOrganizationDetails({
          name: details.organizationName,
          logo: details.organizationLogo,
        });

        // Set the inviter details
        setInviterDetails({
          name: details.inviterName,
          email: details.inviterEmail,
        });

        // Check for logged in user mismatch
        if (user && details.userId !== user.id) {
          setIsUserMismatch(true);
          setIsLoading(false);
          return;
        }

        // Set the existing user details (if any)
        if (details.userId) {
          setUserDetails({
            id: details.userId,
            name: details.userName!,
            avatar: details.userAvatar!,
          });
        }

        setIsLoading(false);
      } catch (error: any) {
        console.warn(error.message);
        setIsInvalid(true);
        setIsLoading(false);
      }
    };

    initInvitation();
  }, [invitation, user, isHydrated]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));

    if (id === "name" && value !== "") {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  };

  const handleAvatarChange = (avatar: string) => {
    setFormData((prev) => ({
      ...prev,
      avatar,
    }));
  };

  const Message: React.FC<any> = () => {
    return (
      <p>
        You&apos;ve been invited by{" "}
        <b className="text-gray-1100 dark:text-gray-100">
          {getUserName(
            inviterDetails?.name || "UNASSIGNED",
            inviterDetails?.email,
          )}
        </b>{" "}
        (
        <Link
          href={`mailto:${inviterDetails?.email}`}
          className="text-brand-accent hover:underline"
        >
          {inviterDetails?.email}
        </Link>
        ) to join the{" "}
        <b className="text-gray-1100 dark:text-gray-100">
          {organizationDetails?.name}
        </b>{" "}
        team on Libum Portal.
      </p>
    );
  };

  interface FooterProps {
    isDisabled?: boolean;
  }

  const Footer: React.FC<FooterProps> = ({ isDisabled = false }) => {
    return (
      <div className="ml-auto flex items-center gap-2">
        {isNavigating && (
          <LoaderCircle className="flex size-4 animate-spin text-brand-primary" />
        )}
        <DialogClose asChild>
          <Button
            variant="form"
            onClick={async () => {
              setIsNavigating(true);
              await onDecline({
                isDeclined: true,
                isExistingUser: !!userDetails,
                organizationId: invitation?.orgId,
                userId: invitation?.userId,
                token: invitation?.token,
              });
            }}
          >
            Decline
          </Button>
        </DialogClose>
        <DialogClose asChild>
          <Button
            disabled={isDisabled}
            onClick={async () => {
              setIsNavigating(true);
              onAccept({
                isDeclined: false,
                isExistingUser: !!userDetails,
                organizationId: invitation?.orgId,
                userId: invitation?.userId,
                token: invitation?.token,
                name: formData.name,
                email: formData.email,
                avatar: formData.avatar.split("/").pop(),
              });
            }}
          >
            {userDetails ? "Accept" : "Create user & accept"}
          </Button>
        </DialogClose>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="bg-card">
        <DialogHeader className="flex">
          {isLoading ? (
            <>
              <DialogTitle className="text-center text-xl text-gray-1100 dark:text-gray-100">
                Fetching your invitation..
              </DialogTitle>
              <DialogDescription />
              <LoaderCircle className="mx-auto animate-spin text-brand-primary" />
            </>
          ) : isInvalid ? (
            <>
              <DialogTitle className="text-center text-xl text-gray-1100 dark:text-gray-100">
                Uh oh! Something went wrong
              </DialogTitle>
              <DialogDescription />
              <div className="flex flex-col items-center py-4 text-center">
                <Image
                  className="mb-4"
                  src="/emojis/monocle-face.svg"
                  alt=""
                  width={24}
                  height={24}
                />
                <p>
                  Hmm, this invitation seems to be invalid. Please{" "}
                  <b>close this tab</b> & ask for a <b>new invitation</b>.
                </p>
              </div>
            </>
          ) : isUserMismatch ? (
            <>
              <DialogTitle className="text-center text-xl text-gray-1100 dark:text-gray-100">
                Freeze! Not so fast...
              </DialogTitle>
              <DialogDescription />
              <div className="flex flex-col items-center py-4 text-center">
                <Image
                  className="mb-4"
                  src="/emojis/cold-face.svg"
                  alt=""
                  width={24}
                  height={24}
                />
                <p>
                  This invitation was meant for another user. Please sign in as
                  the intended user and try again.
                </p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                {isNavigating && (
                  <LoaderCircle className="flex size-4 animate-spin text-brand-primary" />
                )}
                <DialogClose asChild>
                  <Button
                    variant="form"
                    onClick={async () => {
                      setIsNavigating(true);
                      await onDecline(null);
                    }}
                  >
                    Dismiss
                  </Button>
                </DialogClose>
                <DialogClose asChild>
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      setIsNavigating(true);
                      deleteCookie("inviteParams", "/");
                      logout();
                    }}
                  >
                    Sign out
                  </Button>
                </DialogClose>
              </div>
            </>
          ) : userDetails !== null ? (
            <>
              <DialogTitle className="text-center text-xl text-gray-1100 dark:text-gray-100">
                <Avatar className="mx-auto mb-4 size-12">
                  <AvatarImage
                    src={organizationDetails?.logo}
                    alt=""
                  ></AvatarImage>
                </Avatar>
                Join the <b>{organizationDetails?.name}</b> team!
              </DialogTitle>
              <DialogDescription />
              <div className="flex flex-col py-4 text-center">
                <Message />
                <div className="flex items-center justify-center gap-2 py-8">
                  <div className="flex flex-row items-center gap-2">
                    <Avatar className="size-14 border-2 border-brand-primary">
                      <AvatarImage
                        src={`/avatars/${userDetails?.avatar || getRandomAvatar()}`}
                        alt=""
                      ></AvatarImage>
                    </Avatar>
                    <div className="flex flex-col">
                      <div className="flex items-center font-medium text-gray-1100 dark:text-gray-100">
                        {userDetails?.name === "UNASSIGNED"
                          ? invitation?.userId
                          : userDetails?.name}
                      </div>
                      {userDetails?.name &&
                        userDetails.name !== "UNASSIGNED" && (
                          <div className="flex text-sm text-muted">
                            {invitation?.userId}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
                <Footer />
              </div>
            </>
          ) : (
            <>
              <DialogTitle className="text-center text-xl text-gray-1100 dark:text-gray-100">
                <Avatar className="mx-auto mb-4 size-12">
                  <AvatarImage
                    src={organizationDetails?.logo}
                    alt=""
                  ></AvatarImage>
                </Avatar>
                Join the <b>{organizationDetails?.name}</b> team!
              </DialogTitle>
              <DialogDescription />
              <div className="flex flex-col py-4 text-center">
                <Message />
                <div className="flex items-center justify-center gap-2 py-8">
                  <form className="w-full">
                    <div className="grid grid-cols-4 gap-6">
                      <div className="col-span-4 text-left sm:col-span-2">
                        <Label htmlFor="name" className="block">
                          Name
                        </Label>
                        <Input
                          id="name"
                          placeholder="Explorer Dude"
                          type="text"
                          autoComplete="off"
                          value={formData.name}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="col-span-4 text-left sm:col-span-2">
                        <Label htmlFor="email" className="block">
                          Email address
                        </Label>
                        <Input
                          id="email"
                          placeholder="explorer@libum.io"
                          type="text"
                          value={formData.email}
                          autoComplete="off"
                          disabled
                        />
                        <span className="text-xs">
                          Already have an account?{" "}
                          <span
                            onClick={() => router.refresh()}
                            className="cursor-pointer text-brand-accent hover:underline"
                          >
                            Click here
                          </span>
                        </span>
                      </div>
                    </div>
                    <div className="col-span-4 text-left">
                      <Label htmlFor="avatar" className="block">
                        Your Avatar
                      </Label>
                      <div className="mt-2 flex">
                        <AvatarPopover
                          avatar={formData.avatar}
                          onAvatarChange={handleAvatarChange}
                          type="user"
                        />
                      </div>
                    </div>
                  </form>
                </div>
                <Footer isDisabled={isDisabled} />
              </div>
            </>
          )}
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}
